import { FC, Fragment, ReactElement, ReactNode } from "react";
import { styled } from "@mui/material/styles";
import { parseRichText, RichTextNode } from "@/utils/rich-text";

type RichTextProps = {
  /** Text with `<b>`, `<u>`, `\n` markup */
  text?: string | null;
  /** Renders each plain-text leaf (default: raw string) */
  renderText?: (value: string) => ReactNode;
  /** Single-line context (e.g. `noWrap`): render line breaks as spaces */
  inline?: boolean;
};

const defaultRenderText = (value: string): ReactNode => value;

/** Renders bold / underline / line break markup as React elements (no innerHTML). */
export const RichText: FC<RichTextProps> = ({ text, renderText = defaultRenderText, inline = false }): ReactElement | null => {
  if (!text) return null;

  const renderNodes = (nodes: RichTextNode[], keyPrefix = "n"): ReactNode =>
    nodes.map((node, i) => {
      const key = `${keyPrefix}-${i}`;
      switch (node.type) {
        case "text":
          return <Fragment key={key}>{renderText(node.value)}</Fragment>;
        case "break":
          return inline ? " " : <br key={key} />;
        case "bold":
          return <b key={key}>{renderNodes(node.children, key)}</b>;
        case "underline":
          return <u key={key}>{renderNodes(node.children, key)}</u>;
        default:
          return null;
      }
    });

  return <TextWrapper>{renderNodes(parseRichText(text))}</TextWrapper>;
};

const TextWrapper = styled("span")({
  "& b": { fontWeight: 700 },
  "& u": { textDecoration: "underline", textUnderlineOffset: "0.2em" },
});
